import { Dispatch, SetStateAction } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';

import { useMutation } from '@hooks/reactQuery';

import {
	runHistoryProgress,
	runHistoryProgressId as launchHistoryProgress,
} from '@api/hitoryCheck';

import { formatDate } from '@utils/date';
import { handleErrorFromMessage } from '@utils/handleRequestError';
import { startOfDayInUTC } from '@utils/kpi-utils/date';

import {
	ICMMavenirVendor,
} from '@interfaces/cmModule/cmModule';
import { THistoryCheckBranchesDTO } from '@interfaces/cmModule/dto/historyCheckDTO';
import {
	THistoryCheckDefaultBranchesProps,
	THistoryCheckMavenirBranchesProps,
} from '@interfaces/cmModule/historyCheck';
import { IResponseError } from '@interfaces/errors/apiErrors';

import { HISTORY_CHECK_RUN_PROGRESS } from '@constants/queryKeys';

type TUseMutateRunHistoryProgressId = THistoryCheckDefaultBranchesProps
	& THistoryCheckMavenirBranchesProps
	& ICMMavenirVendor
	& {
	setIsProgressRun: Dispatch<SetStateAction<boolean>>;
	generateBranches: UseMutateFunction<unknown, unknown, THistoryCheckBranchesDTO>;
};

type TUseMutateRunHistoryProgressIdReturn = {
	generateProgressId: UseMutateFunction<string, any, void, any>;
	progressId?: string;
};

export const useMutateRunHistoryProgressId = (
	params: Omit<TUseMutateRunHistoryProgressId, 'progressId'>,
): TUseMutateRunHistoryProgressIdReturn => {
	const {
		dateFrom,
		dateTo,
		setIsProgressRun,
		siteIds,
		nodeType,
		technology,
		vendor,
		version,
		nodes,
		type,
		isMavenirVendor,
	} = params;

	const isMavenirDtoValid = !!nodes && !!type && !!version && isMavenirVendor;
	const isDefaultDtoValid = technology && nodeType && !isMavenirVendor;

	const isVendorDtoValid = vendor && dateFrom && dateTo && (isMavenirDtoValid || isDefaultDtoValid);

	const {
		data: progressId,
		mutate: generateProgressId,
	} = useMutation<void, string>({
		mutationKey: [HISTORY_CHECK_RUN_PROGRESS],
		mutationFn: launchHistoryProgress,
		onSuccess: async (id) => {
			if (id && isVendorDtoValid) {
				const mavenirDto = {
					nodes,
					type,
					version,
				};

				const defaultDto = {
					nodeType,
					technology,
					siteIds,
				};

				const dto = {
					dateFrom: startOfDayInUTC(new Date(dateFrom)),
					dateFromText: formatDate(new Date(dateFrom)),
					dateTo: startOfDayInUTC(new Date(dateTo)),
					dateToText: formatDate(new Date(dateTo)),
					vendor,
					progressId: id,
					...(isMavenirVendor ? mavenirDto : defaultDto),
				};

				await runHistoryProgress(dto);

				setIsProgressRun(true);
			} else {
				setIsProgressRun(false);
			}
		},
		onError: (err: IResponseError) => {
			setIsProgressRun(false);
			handleErrorFromMessage(err);
		},
	});

	return {
		generateProgressId,
		progressId,
	};
};
